import { IconButton, ListItemIcon, ListItemText, Box, MenuItem } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { MoreVert, PushPinOutlined } from '@mui/icons-material';
import { useContext, useState } from 'react';
import { PanelFormsContext } from 'contexts/panelFormsContext';
import { openSnackbar } from 'store/slices/snackbar';
import { useDispatch } from 'store';
import { StyledMenu } from 'ui-component/RecordView/components/StyledMenu';
import { pinWay } from '../../utils';

type FieldMenuProps = {
    field: string;
    objectName?: string;
};

export const FieldMenu = ({ field, objectName }: FieldMenuProps) => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const maxPinnedFields = 5;
    const { setPinnedFields, pinnedFields } = useContext(PanelFormsContext);

    const normalFields = pinnedFields.filter((pinnedField) => pinnedField.includes('::') && !pinnedField.includes(';;'));
    const objectFields = pinnedFields.filter((pinnedField) => pinnedField.includes(';;'));

    const isObjectField = objectName !== undefined;
    const [hoverMoreVertAnchorEl, setHoverMoreVertAnchorEl] = useState<HTMLElement | null>(null);
    const newField = objectName ? `${objectName};;${field}` : field;

    const showLimitMessage = () => {
        const fieldType = isObjectField ? 'object fields' : 'fields';
        dispatch(
            openSnackbar({
                open: true,
                message: `You can only pin to a maximum of ${maxPinnedFields} ${fieldType}`,
                variant: 'alert',
                alert: {
                    color: 'warning'
                },
                close: true
            })
        );
    };

    const handlePinUp = () => {
        const currentFieldCount = isObjectField ? objectFields.length : normalFields.length;
        if (currentFieldCount < maxPinnedFields) {
            setPinnedFields?.([`${newField}${pinWay.up}`, ...pinnedFields]);
            return;
        }
        showLimitMessage();
    };

    const handlePinDown = () => {
        const currentFieldCount = isObjectField ? objectFields.length : normalFields.length;

        if (currentFieldCount < maxPinnedFields) {
            setPinnedFields?.([...pinnedFields, `${newField}${pinWay.down}`]);
            return;
        }
        showLimitMessage();
    };

    const replacedField = (fieldName: string) => fieldName.replace(pinWay.up, '').replace(pinWay.down, '');

    const isPinned = isObjectField
        ? objectFields.some((fieldName) => newField === replacedField(fieldName))
        : normalFields.some((fieldName) => newField === replacedField(fieldName));

    const handleUnpin = () => {
        if (isPinned) {
            const newPinnedFields = pinnedFields.filter((fieldName) => {
                const [label] = fieldName.split('::');
                return !newField.includes(label);
            });
            setPinnedFields?.(newPinnedFields);
        }
    };

    return (
        <>
            <IconButton
                onClick={(e) => setHoverMoreVertAnchorEl(e.currentTarget)}
                sx={{
                    height: '2rem',
                    width: '2rem',
                    bgcolor: hoverMoreVertAnchorEl ? theme.palette.secondary.light : 'transparent',
                    color: hoverMoreVertAnchorEl ? theme.palette.primary.dark : 'transparent',
                    '&:hover': {
                        bgcolor: theme.palette.secondary.light,
                        color: theme.palette.primary.dark
                    }
                }}
            >
                {isPinned ? (
                    <PushPinOutlined sx={{ fontSize: '1rem', color: theme.palette.primary[400], transform: 'rotate(35deg)' }} />
                ) : (
                    <MoreVert sx={{ fontSize: '1.25rem' }} />
                )}
            </IconButton>
            <StyledMenu
                open={!!hoverMoreVertAnchorEl}
                anchorEl={hoverMoreVertAnchorEl}
                onClose={() => setHoverMoreVertAnchorEl(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Box onMouseLeave={() => setHoverMoreVertAnchorEl(null)}>
                    {isPinned ? (
                        <MenuItem onClick={handleUnpin}>
                            <ListItemIcon>
                                <PushPinOutlined
                                    sx={{ fontSize: '1.25rem', color: theme.palette.primary[400], transform: 'rotate(-35deg)' }}
                                />
                            </ListItemIcon>
                            <ListItemText>Unpin</ListItemText>
                        </MenuItem>
                    ) : (
                        <>
                            <MenuItem onClick={handlePinUp}>
                                <ListItemIcon>
                                    <PushPinOutlined
                                        sx={{ fontSize: '1.25rem', color: theme.palette.primary[400], transform: 'rotate(35deg)' }}
                                    />
                                </ListItemIcon>
                                <ListItemText>Pin up</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={handlePinDown}>
                                <ListItemIcon>
                                    <PushPinOutlined
                                        sx={{ fontSize: '1.25rem', color: theme.palette.primary[400], transform: 'rotate(-35deg)' }}
                                    />
                                </ListItemIcon>
                                <ListItemText>Pin down</ListItemText>
                            </MenuItem>
                        </>
                    )}
                </Box>
            </StyledMenu>
        </>
    );
};
